import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatCard, usePoll, fmtUSDT, fmtINR } from "@/components/common";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  usePoll(() => api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}), 6000, []);
  if (!d) return <div className="text-slate-400">Loading…</div>;

  const cards = [
    { label: "Total customers", value: d.total_customers, testid: "stat-customers" },
    { label: "Available USDT", value: fmtUSDT(d.total_available_usdt), tone: "blue", testid: "stat-available" },
    { label: "Held USDT", value: fmtUSDT(d.total_held_usdt), tone: "amber", testid: "stat-held" },
    { label: "Deficit USDT", value: fmtUSDT(d.total_deficit_usdt), tone: parseFloat(d.total_deficit_usdt) > 0 ? "rose" : "default", testid: "stat-deficit" },
    { label: "Deposits pending", value: d.deposits_pending, tone: d.deposits_pending ? "amber" : "default", testid: "stat-deposits-pending" },
    { label: "Orders pending", value: d.orders_pending, testid: "stat-orders-pending" },
    { label: "Active timers", value: d.active_timers, tone: "blue", testid: "stat-active-timers" },
    { label: "Expired confirmations", value: d.expired_confirmations, tone: d.expired_confirmations ? "rose" : "default", testid: "stat-expired" },
    { label: "Disputes", value: d.disputes, tone: d.disputes ? "rose" : "default", testid: "stat-disputes" },
    { label: "Excess deductions", value: `${d.excess_deductions_count} · ${fmtUSDT(d.excess_deductions_usdt)}`, testid: "stat-excess" },
    { label: "Open tickets", value: d.open_tickets, testid: "stat-tickets" },
    { label: "Email failures", value: d.email_failures, tone: d.email_failures ? "amber" : "default", testid: "stat-email" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Admin Dashboard</h1>

      <div className="grid sm:grid-cols-3 gap-5">
        <StatCard testid="admin-expected-inr" label="Expected INR (open)" value={fmtINR(d.expected_inr)} />
        <StatCard testid="admin-paid-inr" label="INR paid" value={fmtINR(d.paid_inr)} tone="green" />
        <StatCard testid="admin-remaining-inr" label="Remaining INR" value={fmtINR(d.remaining_inr)} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-2xl border p-4 ${c.tone === "rose" ? "bg-rose-50 border-rose-200" : c.tone === "amber" ? "bg-amber-50 border-amber-200" : c.tone === "blue" ? "bg-blue-50 border-blue-200" : "bg-white border-slate-200"}`} data-testid={c.testid}>
            <div className="text-xs uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className="mt-1.5 text-xl font-bold font-mono text-slate-900">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Link to="/admin/deposits" className="bg-blue-600 text-white rounded-2xl p-5 flex items-center justify-between hover:bg-blue-700 transition">
          <span className="font-semibold">Verification queue</span>
          <span className="font-mono text-2xl">{d.deposits_pending}</span>
        </Link>
        <Link to="/admin/tickets" className="bg-slate-900 text-white rounded-2xl p-5 flex items-center justify-between hover:bg-slate-800 transition">
          <span className="font-semibold">Open support tickets</span>
          <span className="font-mono text-2xl">{d.open_tickets}</span>
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-heading font-semibold text-slate-900 mb-3">Recent audit events</h2>
        <div className="space-y-1.5 text-sm" data-testid="admin-recent-audit">
          {d.recent_audit.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-1.5 border-b border-slate-100">
              <span className="font-medium text-slate-700">{a.action}</span>
              <span className="text-xs text-slate-400">{a.actor_role} · {new Date(a.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
